import { useState, useEffect } from 'react';
import { collection, query, where, limit, onSnapshot } from 'firebase/firestore';
import { parseISO } from 'date-fns';
import { db } from '../firebase/config';
import { dateService } from '../services/dateService';

export function useProverbData(date = new Date()) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isSubscribed = true; // Track if component is mounted
    setLoading(true);

    const fetchData = async () => {
      try {
        // Convert string dates to Date objects
        const dateObj = typeof date === 'string' ? parseISO(date) : date;

        // Use dateService to calculate source day of year (maps 2026 to 2025 content)
        const dayOfYear = dateService.getSourceDayOfYear(dateObj);

        console.log('🔄 Fetching devotional:', {
          date: dateObj.toISOString(),
          year: dateObj.getFullYear(),
          dayOfYear,
          displayDate: dateService.formatDateForCSV(dateObj),
          timestamp: new Date().toISOString(),
          action: 'Setting up Firestore listener'
        });

        const devotionalsRef = collection(db, 'devotionals');
        const q = query(
          devotionalsRef,
          where('day', '==', dayOfYear),
          limit(1)
        );

        const unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            if (!isSubscribed) return; // Don't update state if unmounted

            if (!snapshot.empty) {
              const doc = snapshot.docs[0];
              const devotionalData = {
                ...doc.data(),
                id: doc.id,
                fetchedAt: new Date().toISOString(),
                dayOfYear,
                displayDate: dateService.formatDateForCSV(dateObj)
              };
              console.log('📖 Devotional update received:', {
                action: 'Received update',
                id: devotionalData.id,
                day: devotionalData.day,
                displayDate: devotionalData.displayDate,
                lastModified: devotionalData.lastModified,
                timestamp: new Date().toISOString()
              });
              setData(devotionalData);
            } else {
              console.log('❌ No devotional found:', {
                dayOfYear,
                displayDate: dateService.formatDateForCSV(dateObj),
                timestamp: new Date().toISOString()
              });
              setData(null);
            }
            setLoading(false);
          },
          (err) => {
            if (!isSubscribed) return; // Don't update state if unmounted

            console.error('🚨 Firestore error:', {
              error: err,
              dayOfYear,
              displayDate: dateService.formatDateForCSV(dateObj),
              timestamp: new Date().toISOString()
            });
            setError(err.message);
            setLoading(false);
          }
        );

        return () => {
          console.log('🧹 Cleaning up Firestore listener:', {
            dayOfYear,
            displayDate: dateService.formatDateForCSV(dateObj),
            timestamp: new Date().toISOString()
          });
          isSubscribed = false;
          unsubscribe();
        };
      } catch (err) {
        if (!isSubscribed) return; // Don't update state if unmounted

        console.error('🚨 Hook error:', {
          error: err,
          date,
          timestamp: new Date().toISOString()
        });
        setError(err.message);
        setLoading(false);
      }
    };

    const cleanup = fetchData();
    return () => {
      if (cleanup && typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [date]);

  return { proverbData: data, loading, error };
} 